import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useParams } from "react-router-dom";
import moment from "moment";
import Typography from "@mui/material/Typography";
import Breadcrumbs from "@mui/material/Breadcrumbs";

import theme from "../../styles/theme";
import Link from "../../components/Common/Link";
import { Post } from "../../components/News/Post";
import { getRelationItem, getPostImageProps, POST_IMAGE_SIZES } from "../../utils/helpers/generic";
import { useNewsContext } from "../../contexts/news";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import NewsSEO from "../../components/SEO/NewsSEO";
import NotFound from "../NotFound";

const PostTitleLatest = styled.div`
  font-size: 14px;
  font-family: "Montserrat", sans-serif;
  transition: all 0.3s;
  font-weight: 400;
`;

const LatestNewsContainer = styled.div`
  margin-left: 16px;
  margin-top: 220px;
  height: max-content;
  background: ${theme.colors.darkLight}50;
  padding: 16px;
  border-radius: 4px;
  max-width: 400px;
  position: sticky;
  width: 100%;
  top: 80px;
  flex-direction: column;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  border-left: 3px solid #2a2a2a;
  // padding-left: 16px;

  @media (max-width: ${theme.breakpointsCustom.max}px) {
    display: none;
  }
`;

const LatestNewsTitle = styled.div`
  font-size: 24px;
  font-family: "Montserrat", sans-serif;
`;

const LatestNewsColumn = styled(Link)`
  display: flex;
  padding: 8px 0px;
  gap: 12px;
  border-bottom: 2px solid ${theme.colors.darkLight};
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-bottom: 2px solid ${theme.colors.green};
  }
`;

const StyledImgLatest = styled.img`
  height: fit-content;
  width: 150px;
  border-radius: 4px;
  aspect-ratio: 16 / 9;
  object-fit: cover;
`;

const StyledLinkLatestNews = styled(Link)`
  margin-bottom: 16px;
  text-decoration: none;
  color: ${theme.colors.white};
  font-size: 16px;
  font-weight: 400;
  font-family: "Montserrat", sans-serif;
  display: flex;
  align-items: center;
  margin-top: 16px;
  transition: all 0.3s;

  &:hover {
    color: ${theme.colors.green};
  }
`;

const NewType = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  color: ${(props) => props.color};
`;

const NewTime = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 14px;
  opacity: 0.5;
  color: ${theme.colors.white};
`;

const LatestNewsPostTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const LatestNews = () => {
  const { id } = useParams();
  const { posts } = useNewsContext();
  if (!posts?.length) return;

  return (
    <LatestNewsContainer>
      <LatestNewsTitle>Останні публікації</LatestNewsTitle>
      {posts
        .slice(0, 5)
        .filter((post) => post.id !== Number(id))
        .slice(0, 4)
        .map((post, i) => {
          const category = getRelationItem(post, "category");
          const time = moment(post.publishedAt).fromNow();
          let { title } = post;
          if (title.length > 60) {
            title = title.slice(0, 60).split(" ").slice(0, -1).join(" ") + "...";
          }
          return (
            <LatestNewsColumn key={i} to={`/news/${post.id}`}>
              <StyledImgLatest
                {...getPostImageProps(post, POST_IMAGE_SIZES.small, "150px")}
                loading="lazy"
                decoding="async"
              />
              <LatestNewsPostTitleContainer>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: "4px",
                  }}
                >
                  <NewType color={category.color}>{category.name}</NewType>
                  <NewTime>{time}</NewTime>
                </div>

                <PostTitleLatest>{title}</PostTitleLatest>
              </LatestNewsPostTitleContainer>
            </LatestNewsColumn>
          );
        })}
      <StyledLinkLatestNews to="/news">
        Усі публікації <ChevronRightIcon />
      </StyledLinkLatestNews>
    </LatestNewsContainer>
  );
};

export const NewsPostPage = () => {
  const { id } = useParams();
  const { posts, postsMap, loadPost, loadPosts } = useNewsContext();
  const post = useMemo(() => postsMap[id], [id, postsMap]);
  const [status, setStatus] = useState("loading");
  const location = useLocation();
  const currentUrl = `https://slickracing.com.ua${location.pathname}`;

  useEffect(() => {
    // Only the sidebar needs the list, and it never changes per article. This used to call
    // `loadPosts(id)` as well — an id where a `{ filter }` object is expected, which happened
    // to destructure to an empty filter and refetch all 50 posts on every article view.
    if (!posts.length) {
      loadPosts({});
    }
  }, [id]); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;

    setStatus("loading");
    loadPost(id).then((found) => {
      if (!cancelled) {
        setStatus(found ? "ready" : "missing");
      }
    });

    return () => {
      cancelled = true;
    };
  }, [id]); // eslint-disable-line

  if (!post) {
    // The loading and missing cases must stay apart. They used to share one branch, so every
    // article rendered "post not found" while its fetch was in flight — and now that the
    // branch emits `noindex`, collapsing them would hand Google a noindex for real articles.
    if (status === "missing") {
      return (
        <NotFound
          title="Новину не знайдено"
          text="Такої публікації немає — можливо, в посиланні є помилка. Перегляньте всі новини Slick Racing Ukraine."
        />
      );
    }

    // TODO: add skeleton
    return null;
  }

  return (
    <div style={{ marginTop: 24 }}>
      <NewsSEO post={post} url={currentUrl} />
      <Breadcrumbs aria-label="breadcrumb">
        <Link style={{ underline: "hover", color: "inherit" }} to="/">
          Головна
        </Link>
        <Link style={{ underline: "hover", color: "inherit" }} to="/news">
          Новини
        </Link>
        <Typography sx={{ color: "#c1c1c1", cursor: "default" }}>{post.title}</Typography>
      </Breadcrumbs>
      <div style={{ display: "flex", overflow: "visible" }}>
        <div
          style={{
            maxWidth: "900px",
            marginTop: "24px",
            marginLeft: "auto",
            marginRight: "auto",
          }}
        >
          <Post {...post} />
        </div>
        <LatestNews />
      </div>
    </div>
  );
};

export default NewsPostPage;
